import React, { useState, useMemo } from 'react';
import {
  BookOpen, ChevronDown, ChevronRight, Search, TrendingUp,
  Globe, BarChart3, Calculator, Target, Info, ShoppingCart,
  Truck, DollarSign, ArrowUpRight, Users, Layers, Scale,
  AlertTriangle, CheckCircle, ExternalLink, Lightbulb
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';

/* ─── Reusable building blocks (same pattern as AlgorithmGuide) ──────── */

const Section = ({ icon: Icon, title, description, children, defaultOpen = false, id, highlight = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card id={id} className={`border-border transition-all ${highlight ? 'ring-2 ring-primary/50 shadow-md' : ''}`}>
      <CardHeader className="cursor-pointer select-none" onClick={() => setOpen((prev) => !prev)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              {description && <CardDescription className="mt-1">{description}</CardDescription>}
            </div>
          </div>
          <div className="text-muted-foreground">
            {open ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </div>
        </div>
      </CardHeader>
      {open && <CardContent>{children}</CardContent>}
    </Card>
  );
};

const InfoBox = ({ children, variant = 'default' }) => {
  const styles = {
    default: 'border-border bg-muted/50',
    formula: 'border-primary/20 bg-primary/5',
    note: 'border-yellow-500/30 bg-yellow-500/5',
    important: 'border-red-500/30 bg-red-500/5',
    success: 'border-emerald-500/30 bg-emerald-500/5',
  };
  return <div className={`rounded-lg border p-4 text-sm ${styles[variant]}`}>{children}</div>;
};

const MetricCard = ({ label, value, sub, good }) => (
  <div className={`rounded-lg border p-3 text-center ${good === true ? 'border-emerald-500/30 bg-emerald-500/5' : good === false ? 'border-red-500/30 bg-red-500/5' : 'border-border bg-muted/50'}`}>
    <p className="text-xs text-muted-foreground font-medium">{label}</p>
    <p className="text-lg font-bold text-foreground mt-0.5">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const SourceLink = ({ num, url, title }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
    [{num}] {title} <ExternalLink className="h-3 w-3" />
  </a>
);

/* ─── Data ───────────────────────────────────────────────────────────── */

const ANALYSIS_DATE = 'March 2026';
const TOTAL_ORDERS = '13,810';

const COUNTRIES = [
  {
    iso: 'NL', name: 'Netherlands', flag: '🇳🇱',
    ordersWeek: 264, ordersTotal: 3300,
    aov: 310, median: 183,
    avgShip: 10.15, medianShip: 8.78,
    currentB2C: null, currentB2B: 215,
    recommended: 200,
    freeShipPct: 86.3, qualifyPct: 46.6,
    marginAfter: 24.9, upliftPool: 7.7, avgGap: null,
    reasoning: 'Shipping costs are very low (~€10 avg). A €200 threshold captures ~47% of orders and only costs 2.2% of qualifying revenue in absorption. The median order is €183, so €200 is just above — nudging customers to add one small item. Going higher (€250) would only save ~€2,500/year in absorption but miss 8% more qualifying orders.',
  },
  {
    iso: 'BE', name: 'Belgium', flag: '🇧🇪',
    ordersWeek: 167, ordersTotal: 2552,
    aov: 282, median: 159,
    avgShip: 9.40, medianShip: 7.90,
    currentB2C: 230, currentB2B: null,
    recommended: 230,
    freeShipPct: 83.1, qualifyPct: 36,
    marginAfter: 23.6, upliftPool: 8.9, avgGap: null,
    reasoning: 'Belgium already has the only formal B2C franco and it\'s well-calibrated. Shipping costs are the lowest in the group (€7.90 median). Lowering to €200 would capture 6% more orders at marginal cost — worth considering but not urgent.',
  },
  {
    iso: 'FR', name: 'France', flag: '🇫🇷',
    ordersWeek: 134, ordersTotal: 2622,
    aov: 310, median: 196,
    avgShip: 12.44, medianShip: 10.00,
    currentB2C: null, currentB2B: 275,
    recommended: 250,
    freeShipPct: 89.2, qualifyPct: 39.1,
    marginAfter: 24.1, upliftPool: 10.1, avgGap: 27,
    reasoning: 'France has 50% higher shipping costs than NL/BE, so the threshold needs to be higher. The 10.1% uplift pool is the largest — meaning ~264 orders are just below €250 with an average gap of only €27. These customers are prime candidates to add an item to qualify. Setting it at B2B\'s €275 would be fine too, but €250 captures more orders and the margin stays healthy at 24.1%.',
  },
  {
    iso: 'DE', name: 'Germany', flag: '🇩🇪',
    ordersWeek: 66, ordersTotal: 739,
    aov: 320, median: 212,
    avgShip: 11.81, medianShip: 10.60,
    currentB2C: null, currentB2B: 195,
    recommended: 250,
    freeShipPct: 90.8, qualifyPct: 42.1,
    marginAfter: 24.1, upliftPool: 12.9, avgGap: 32,
    reasoning: 'Germany has the highest uplift potential — 12.9% of orders are just below €250 with an average gap of €32. The median order is already €212, so €250 is very achievable. Note the B2B franco is lower at €195 — you could match that for B2C too, but at €250 the margin stays stronger and the uplift effect is maximized.',
  },
  {
    iso: 'GB', name: 'United Kingdom', flag: '🇬🇧',
    ordersWeek: 56, ordersTotal: 620,
    aov: 324, median: 216,
    avgShip: 29.13, medianShip: 24.70,
    currentB2C: null, currentB2B: 720,
    recommended: 400,
    freeShipPct: 32.6, qualifyPct: 22.9,
    marginAfter: 26.0, upliftPool: 11.1, avgGap: null,
    reasoning: 'UK shipping is 2.5-3x more expensive than EU. At €300, absorption would eat 5.2% of qualifying revenue — too aggressive. At €400, it drops to 4.3% and margin stays at a healthy 26%. The current B2B franco at €720 is very conservative. €400 for B2C hits the sweet spot: high enough to protect margin, low enough to be achievable (free-shipping orders average €587 vs €197 for paid).',
  },
];

const SECTIONS = [
  { id: 'executive-summary', title: 'Executive Summary', keywords: 'summary overview recommendations tldr conclusion' },
  { id: 'country-deep-dive', title: 'Country Deep-Dive', keywords: 'netherlands belgium france germany uk gb nl be fr de country recommendation' },
  { id: 'methodology', title: 'Methodology & Data', keywords: 'methodology data sample size approach calculation how orders' },
  { id: 'industry-benchmarks', title: 'Industry Benchmarks', keywords: 'industry benchmark standard average threshold comparison ecommerce statistics' },
  { id: 'consumer-behavior', title: 'Consumer Behavior Insights', keywords: 'consumer behavior abandonment cart psychology uplift add items free shipping expectations' },
  { id: 'simulations', title: 'Threshold Simulations', keywords: 'simulation model what-if scenario threshold test impact revenue margin' },
  { id: 'b2b-vs-b2c', title: 'B2B vs B2C Considerations', keywords: 'b2b b2c wholesale retail difference comparison business consumer' },
  { id: 'cross-border', title: 'Cross-Border & EU Considerations', keywords: 'cross-border eu uk international ioss vat duty regulation 2026' },
  { id: 'implementation', title: 'Implementation Playbook', keywords: 'implementation rollout plan steps testing ab test monitor kpi' },
  { id: 'sources', title: 'Sources & References', keywords: 'sources references links research data studies' },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const FrancoAnalysis = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const matchingSections = useMemo(() => {
    if (!searchQuery.trim()) return new Set(SECTIONS.map((s) => s.id));
    const q = searchQuery.toLowerCase();
    const matching = new Set();
    for (const s of SECTIONS) {
      if (s.title.toLowerCase().includes(q) || s.keywords.toLowerCase().includes(q)) {
        matching.add(s.id);
      }
    }
    return matching;
  }, [searchQuery]);

  const isVisible = (id) => matchingSections.has(id);
  const isHighlighted = (id) => searchQuery.trim() && matchingSections.has(id);

  return (
    <div className="px-6 py-8">
      {/* ── Page header ──────────────────────────────────────────── */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <TrendingUp className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Franco Analysis</h1>
            <p className="text-sm text-muted-foreground">
              Free-shipping threshold recommendations for the top 5 B2C countries &mdash; backed by {TOTAL_ORDERS} orders and industry research.
            </p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Analysis date: {ANALYSIS_DATE} &middot; Data source: Shippy PackingAnalysisRecord</p>
        <div className="mt-3 max-w-5xl rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100 flex gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            <strong>Live test (franco threshold experiment):</strong> in AT, CZ, PL, PT, IT, DK, ES and IE, half of the
            addresses may get free shipping from EUR 225 instead of the normal EUR 300 while the experiment runs (mainland
            only, carts whose margin with free shipping stays at 26% or more). The storefront&rsquo;s free-shipping bar, if
            any, still shows the normal EUR 300 (known limitation). Status and results: <a href="/experiments" className="underline">Experiments</a>;
            rules: Checkout Guide, &ldquo;Shipping Experiments&rdquo;.
          </span>
        </div>
      </div>

      {/* ── Search ────────────────────────────────────────────────── */}
      <div className="mb-6 max-w-md relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search topics... (e.g. uplift, simulation, UK, B2B)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
        {searchQuery && (
          <p className="text-xs text-muted-foreground mt-1">
            Showing {matchingSections.size} of {SECTIONS.length} sections
          </p>
        )}
      </div>

      <div className="space-y-4 max-w-5xl">

        {/* ════════════════════════════════════════════════════════════
            1. EXECUTIVE SUMMARY
           ════════════════════════════════════════════════════════════ */}
        {isVisible('executive-summary') && (
          <Section
            id="executive-summary"
            icon={Target}
            title="Executive Summary"
            description="TL;DR — recommended thresholds and why they work"
            defaultOpen={!searchQuery}
            highlight={isHighlighted('executive-summary')}
          >
            <div className="space-y-5 text-sm text-foreground leading-relaxed">
              <p>
                This analysis covers the <strong>top 5 B2C markets</strong> by order volume. Today only Belgium (BE) has a formal B2C franco threshold set at <strong>€230</strong>. The remaining four countries have no B2C franco, meaning customers either get free shipping implicitly (when the order exceeds the B2B threshold) or always pay.
              </p>

              {/* Summary table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-3 font-semibold">Country</th>
                      <th className="py-2 pr-3 font-semibold text-right">Current B2C</th>
                      <th className="py-2 pr-3 font-semibold text-right">Recommended</th>
                      <th className="py-2 pr-3 font-semibold text-right">% Qualifying</th>
                      <th className="py-2 pr-3 font-semibold text-right">Margin After</th>
                      <th className="py-2 font-semibold text-right">Uplift Pool</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COUNTRIES.map((c) => (
                      <tr key={c.iso} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="py-2 pr-3 font-medium">{c.flag} {c.name} ({c.iso})</td>
                        <td className="py-2 pr-3 text-right">{c.currentB2C ? `€${c.currentB2C}` : <span className="text-muted-foreground">None</span>}</td>
                        <td className="py-2 pr-3 text-right font-semibold text-primary">€{c.recommended}</td>
                        <td className="py-2 pr-3 text-right">{c.qualifyPct}%</td>
                        <td className="py-2 pr-3 text-right">{c.marginAfter}%</td>
                        <td className="py-2 text-right">{c.upliftPool}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <InfoBox variant="success">
                <p className="font-semibold flex items-center gap-2"><CheckCircle className="h-4 w-4" /> All recommendations satisfy three constraints:</p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-muted-foreground">
                  <li><strong>Margin safety:</strong> contribution margin stays above 23% after absorption</li>
                  <li><strong>Qualification rate:</strong> 22-47% of orders qualify, within the industry-standard 20-50% band</li>
                  <li><strong>Uplift opportunity:</strong> 7-13% of orders sit just below the threshold and can be nudged up</li>
                </ul>
              </InfoBox>

              <InfoBox variant="note">
                <p className="font-semibold flex items-center gap-2"><Lightbulb className="h-4 w-4" /> Key insight</p>
                <p className="mt-1 text-muted-foreground">
                  Industry data shows that <strong>58% of shoppers add extra items</strong> to reach free shipping thresholds, and implementing thresholds typically increases AOV by <strong>15-30%</strong>. Even a conservative 10% uplift on the 7-13% of orders in the "nudge zone" would generate meaningful incremental revenue with virtually zero additional shipping cost.
                </p>
              </InfoBox>
            </div>
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════
            2. COUNTRY DEEP-DIVE
           ════════════════════════════════════════════════════════════ */}
        {isVisible('country-deep-dive') && (
          <Section
            id="country-deep-dive"
            icon={Globe}
            title="Country Deep-Dive"
            description="Detailed analysis and rationale for each country"
            highlight={isHighlighted('country-deep-dive')}
          >
            <div className="space-y-8 text-sm text-foreground leading-relaxed">
              {COUNTRIES.map((c) => (
                <div key={c.iso} className="space-y-4">
                  <h3 className="text-base font-semibold flex items-center gap-2">
                    <span className="text-xl">{c.flag}</span> {c.name} ({c.iso}) &mdash;{' '}
                    {c.currentB2C && c.currentB2C === c.recommended ? (
                      <span className="text-emerald-600 dark:text-emerald-400">Keep €{c.recommended}</span>
                    ) : (
                      <span className="text-primary">Recommend €{c.recommended}</span>
                    )}
                  </h3>

                  {/* Metric cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                    <MetricCard label="Orders (week / total)" value={`${c.ordersWeek} / ${c.ordersTotal.toLocaleString()}`} />
                    <MetricCard label="AOV / Median" value={`€${c.aov} / €${c.median}`} />
                    <MetricCard label="Avg shipping" value={`€${c.avgShip.toFixed(2)}`} sub={`median €${c.medianShip.toFixed(2)}`} />
                    <MetricCard label="Current B2C" value={c.currentB2C ? `€${c.currentB2C}` : 'Not set'} good={c.currentB2C ? true : false} />
                    <MetricCard label="Already free" value={`${c.freeShipPct}%`} sub="of orders" good={c.freeShipPct > 50} />
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <MetricCard label="Qualifying at target" value={`${c.qualifyPct}%`} good={c.qualifyPct >= 20} />
                    <MetricCard label="Margin after absorption" value={`${c.marginAfter}%`} good={c.marginAfter >= 23} />
                    <MetricCard label="Uplift pool" value={`${c.upliftPool}%`} sub={c.avgGap ? `avg gap €${c.avgGap}` : 'of orders near threshold'} />
                    <MetricCard label="Threshold vs median" value={`+${Math.round(((c.recommended - c.median) / c.median) * 100)}%`} sub="above median OV" />
                  </div>

                  <InfoBox>
                    <p className="font-medium">Rationale</p>
                    <p className="mt-1 text-muted-foreground">{c.reasoning}</p>
                  </InfoBox>

                  {c.iso !== 'GB' && <div className="border-b border-border/30" />}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════
            3. METHODOLOGY
           ════════════════════════════════════════════════════════════ */}
        {isVisible('methodology') && (
          <Section
            id="methodology"
            icon={Calculator}
            title="Methodology & Data"
            description="How we calculated the recommendations"
            highlight={isHighlighted('methodology')}
          >
            <div className="space-y-4 text-sm text-foreground leading-relaxed">
              <p>The analysis uses <strong>{TOTAL_ORDERS} successful packing simulations</strong> from Shippy's <code className="text-xs bg-muted px-1 py-0.5 rounded">PackingAnalysisRecord</code> table, filtered by <code className="text-xs bg-muted px-1 py-0.5 rounded">status='success'</code>.</p>

              <h4 className="font-semibold mt-4">Data points per order</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li><strong>Order value</strong> &mdash; total line-item revenue in EUR</li>
                <li><strong>Optimal shipping cost</strong> &mdash; the cheapest packing solution found by Shippy</li>
                <li><strong>Charged shipping cost</strong> &mdash; what was actually charged to the customer</li>
                <li><strong>Country &amp; zone</strong> &mdash; ISO-2 code + special-zone flags</li>
              </ul>

              <h4 className="font-semibold mt-4">Threshold selection criteria</h4>
              <InfoBox variant="formula">
                <p className="font-mono text-xs space-y-2">
                  <span className="block"><strong>1.</strong> Margin safety &ge; 23%</span>
                  <span className="block ml-4">contribution_margin = (order_value &times; gross_margin%) &minus; shipping_cost</span>
                  <span className="block ml-4">margin_after = contribution_margin / order_value</span>
                  <span className="block mt-2"><strong>2.</strong> Qualification rate between 20% and 50%</span>
                  <span className="block ml-4">qualifying_orders = orders WHERE order_value &ge; threshold</span>
                  <span className="block ml-4">qualification_rate = qualifying_orders / total_orders</span>
                  <span className="block mt-2"><strong>3.</strong> Threshold = median_OV &times; (1.09 to 1.85)</span>
                  <span className="block ml-4">Lower multiplier for cheap-shipping countries (NL, BE)</span>
                  <span className="block ml-4">Higher multiplier for expensive-shipping countries (GB)</span>
                  <span className="block mt-2"><strong>4.</strong> Absorption &lt; 5% of qualifying revenue</span>
                  <span className="block ml-4">absorption_rate = total_absorbed_shipping / total_qualifying_revenue</span>
                </p>
              </InfoBox>

              <h4 className="font-semibold mt-4">Uplift pool calculation</h4>
              <p className="text-muted-foreground">
                The "uplift pool" is the percentage of orders whose value falls within <strong>80-100% of the threshold</strong> (i.e., within 20% below).
                These are the orders most likely to be "nudged" up to the threshold by adding a small item. The average gap is the mean difference between these orders and the threshold.
              </p>

              <InfoBox variant="note">
                <p className="font-semibold">Assumptions</p>
                <ul className="list-disc list-inside mt-1 text-muted-foreground space-y-1">
                  <li>Gross margin is estimated at ~30% across all countries (pre-shipping)</li>
                  <li>Shipping costs use Shippy's optimal (cheapest) simulation, not charged costs</li>
                  <li>Currency conversions (GBP) use the rate at time of order</li>
                  <li>B2B orders are excluded from the B2C analysis</li>
                </ul>
              </InfoBox>
            </div>
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════
            4. INDUSTRY BENCHMARKS
           ════════════════════════════════════════════════════════════ */}
        {isVisible('industry-benchmarks') && (
          <Section
            id="industry-benchmarks"
            icon={BarChart3}
            title="Industry Benchmarks"
            description="How our thresholds compare to industry standards"
            highlight={isHighlighted('industry-benchmarks')}
          >
            <div className="space-y-5 text-sm text-foreground leading-relaxed">

              <h4 className="font-semibold">Free shipping adoption (2025-2026)</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <MetricCard label="Retailers offering free shipping" value="80-83%" sub="of all e-commerce" />
                <MetricCard label="Use threshold-based" value="45%" sub="conditional on spend" />
                <MetricCard label="Offer unconditional" value="20%" sub="free on every order" />
                <MetricCard label="Median US threshold" value="$64" sub="up 23% since 2019" />
              </div>

              <h4 className="font-semibold mt-4">Our thresholds vs industry norms</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-3 font-semibold">Country</th>
                      <th className="py-2 pr-3 font-semibold text-right">Our threshold</th>
                      <th className="py-2 pr-3 font-semibold text-right">Median OV</th>
                      <th className="py-2 pr-3 font-semibold text-right">% above median</th>
                      <th className="py-2 pr-3 font-semibold text-right">Industry norm</th>
                      <th className="py-2 font-semibold text-center">Verdict</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COUNTRIES.map((c) => {
                      const pctAbove = Math.round(((c.recommended - c.median) / c.median) * 100);
                      const inRange = pctAbove >= 9 && pctAbove <= 85;
                      return (
                        <tr key={c.iso} className="border-b border-border/50">
                          <td className="py-2 pr-3">{c.flag} {c.iso}</td>
                          <td className="py-2 pr-3 text-right font-medium">€{c.recommended}</td>
                          <td className="py-2 pr-3 text-right">€{c.median}</td>
                          <td className="py-2 pr-3 text-right">+{pctAbove}%</td>
                          <td className="py-2 pr-3 text-right text-muted-foreground">15-30% above</td>
                          <td className="py-2 text-center">
                            {inRange ? (
                              <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                                <CheckCircle className="h-3.5 w-3.5" /> In range
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-yellow-600 dark:text-yellow-400 text-xs font-medium">
                                <AlertTriangle className="h-3.5 w-3.5" /> High (justified)
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <InfoBox variant="note">
                <p className="text-muted-foreground">
                  <strong>UK is the outlier</strong> at +85% above median &mdash; but this is justified by shipping costs that are 2.5-3x higher than EU destinations.
                  The industry norm of 15-30% assumes domestic shipping; for cross-border heavy-parcel destinations, higher multipliers are standard.
                </p>
              </InfoBox>

              <h4 className="font-semibold mt-4">Key industry statistics</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-3 font-semibold">Metric</th>
                      <th className="py-2 pr-3 font-semibold">Value</th>
                      <th className="py-2 font-semibold">Source</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">Shoppers who add items to qualify</td><td className="py-1.5 pr-3 font-medium text-foreground">58-93%</td><td className="py-1.5 text-xs">Capital One, UPS [1][2]</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">AOV increase with thresholds</td><td className="py-1.5 pr-3 font-medium text-foreground">15-30%</td><td className="py-1.5 text-xs">Rework, Red Stag [3][4]</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">Cart abandonment due to shipping</td><td className="py-1.5 pr-3 font-medium text-foreground">39-48%</td><td className="py-1.5 text-xs">Baymard, eMarketer [5][6]</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">Consumers expecting free shipping</td><td className="py-1.5 pr-3 font-medium text-foreground">66-80%</td><td className="py-1.5 text-xs">Red Stag, SellersCommerce [4][7]</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">Conversion rate lift from free shipping</td><td className="py-1.5 pr-3 font-medium text-foreground">~20%</td><td className="py-1.5 text-xs">GoInflow [8]</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">Prefer free over fast shipping</td><td className="py-1.5 pr-3 font-medium text-foreground">88%</td><td className="py-1.5 text-xs">Swell [9]</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">Carrier rate increases (annual, 2024-26)</td><td className="py-1.5 pr-3 font-medium text-foreground">5.9%/year</td><td className="py-1.5 text-xs">PartnerShip [10]</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">Consumer willingness to wait +2 days for free</td><td className="py-1.5 pr-3 font-medium text-foreground">83%</td><td className="py-1.5 text-xs">ComScore/UPS [2]</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════
            5. CONSUMER BEHAVIOR
           ════════════════════════════════════════════════════════════ */}
        {isVisible('consumer-behavior') && (
          <Section
            id="consumer-behavior"
            icon={ShoppingCart}
            title="Consumer Behavior Insights"
            description="Why free shipping thresholds work — the psychology"
            highlight={isHighlighted('consumer-behavior')}
          >
            <div className="space-y-5 text-sm text-foreground leading-relaxed">

              <h4 className="font-semibold">The psychology of free shipping</h4>
              <p className="text-muted-foreground">
                Free shipping is the most powerful promotional lever in e-commerce. <strong>92% of consumers</strong> are influenced by free shipping offers [7], and <strong>90%</strong> cite it as their top incentive to buy more [3]. It consistently outperforms percentage discounts because it removes a perceived loss (paying for "nothing") rather than adding a gain (a discount).
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                <InfoBox>
                  <p className="font-semibold text-center text-lg text-primary">58%</p>
                  <p className="text-center text-muted-foreground mt-1">of shoppers add extra items to reach the threshold [2]</p>
                </InfoBox>
                <InfoBox>
                  <p className="font-semibold text-center text-lg text-primary">48%</p>
                  <p className="text-center text-muted-foreground mt-1">of cart abandonment is due to shipping fees [6]</p>
                </InfoBox>
                <InfoBox>
                  <p className="font-semibold text-center text-lg text-primary">83%</p>
                  <p className="text-center text-muted-foreground mt-1">will wait 2 extra days if shipping is free [2]</p>
                </InfoBox>
              </div>

              <h4 className="font-semibold mt-4">The "magnet effect"</h4>
              <p className="text-muted-foreground">
                The pull to add items is strongest when the gap between the cart and the threshold is <strong>€5-€25</strong> [3]. Above that, customers feel the gap is too large to bridge. Below it, they already qualify. This is why our thresholds are calibrated close to the median order value &mdash; to maximize the number of customers in this sweet spot.
              </p>

              <InfoBox variant="formula">
                <p className="font-semibold">How this applies to our data</p>
                <div className="mt-2 space-y-2 text-muted-foreground">
                  <p><strong>Germany:</strong> 12.9% of orders are within 20% below €250 (avg gap: €32). Even if only half of these add items, that's ~47 extra orders/week converting at a higher AOV.</p>
                  <p><strong>France:</strong> 10.1% uplift pool with avg gap of just €27 — well within the magnet zone. ~264 orders in total are prime candidates.</p>
                  <p><strong>Netherlands:</strong> Median OV of €183 vs threshold of €200 means most customers are just one small item away.</p>
                </div>
              </InfoBox>

              <h4 className="font-semibold mt-4">The Amazon effect</h4>
              <p className="text-muted-foreground">
                <strong>78% of Amazon Prime members</strong> cite free shipping as the primary reason for membership [11]. This has created a baseline expectation: consumers now judge every checkout against Amazon's standard. For our furniture/home-goods category, <strong>66% of consumers expect free shipping on all orders</strong> regardless of amount [4]. By introducing formal thresholds, we acknowledge this expectation while protecting margins.
              </p>

              <h4 className="font-semibold mt-4">Speed vs. cost tradeoff</h4>
              <p className="text-muted-foreground">
                A key finding: <strong>88% of consumers prioritize free shipping over fast delivery</strong> [9], and <strong>95% prefer free standard shipping over paid express</strong> [2]. This means we can use standard/economy carriers for free-shipping orders without hurting satisfaction — a direct cost savings opportunity.
              </p>
            </div>
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════
            6. SIMULATIONS
           ════════════════════════════════════════════════════════════ */}
        {isVisible('simulations') && (
          <Section
            id="simulations"
            icon={Scale}
            title="Threshold Simulations"
            description="What-if scenarios — what happens at different thresholds?"
            highlight={isHighlighted('simulations')}
          >
            <div className="space-y-5 text-sm text-foreground leading-relaxed">

              <p>
                The tables below show how key metrics change as we move the threshold up or down from the recommended value. This helps visualize the trade-offs at each level.
              </p>

              {/* ── NL Simulation ── */}
              <h4 className="font-semibold mt-2">Netherlands (NL) — scenarios</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-3 font-semibold">Threshold</th>
                      <th className="py-2 pr-3 font-semibold text-right">% Qualifying</th>
                      <th className="py-2 pr-3 font-semibold text-right">Absorption</th>
                      <th className="py-2 pr-3 font-semibold text-right">Margin</th>
                      <th className="py-2 pr-3 font-semibold text-right">Uplift pool</th>
                      <th className="py-2 font-semibold text-center">Rating</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€150</td><td className="py-1.5 pr-3 text-right">58%</td><td className="py-1.5 pr-3 text-right">3.1%</td><td className="py-1.5 pr-3 text-right">23.2%</td><td className="py-1.5 pr-3 text-right">5.8%</td><td className="py-1.5 text-center text-yellow-500">Tight margin</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€175</td><td className="py-1.5 pr-3 text-right">52%</td><td className="py-1.5 pr-3 text-right">2.6%</td><td className="py-1.5 pr-3 text-right">24.1%</td><td className="py-1.5 pr-3 text-right">6.8%</td><td className="py-1.5 text-center text-muted-foreground">Acceptable</td></tr>
                    <tr className="border-b border-border/50 bg-primary/5"><td className="py-1.5 pr-3 font-semibold text-primary">€200 *</td><td className="py-1.5 pr-3 text-right font-semibold">46.6%</td><td className="py-1.5 pr-3 text-right font-semibold">2.2%</td><td className="py-1.5 pr-3 text-right font-semibold">24.9%</td><td className="py-1.5 pr-3 text-right font-semibold">7.7%</td><td className="py-1.5 text-center text-emerald-500 font-semibold">Recommended</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€250</td><td className="py-1.5 pr-3 text-right">38%</td><td className="py-1.5 pr-3 text-right">1.8%</td><td className="py-1.5 pr-3 text-right">25.6%</td><td className="py-1.5 pr-3 text-right">5.1%</td><td className="py-1.5 text-center text-muted-foreground">Too high</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€300</td><td className="py-1.5 pr-3 text-right">31%</td><td className="py-1.5 pr-3 text-right">1.5%</td><td className="py-1.5 pr-3 text-right">26.2%</td><td className="py-1.5 pr-3 text-right">4.2%</td><td className="py-1.5 text-center text-muted-foreground">Misses volume</td></tr>
                  </tbody>
                </table>
              </div>

              {/* ── FR Simulation ── */}
              <h4 className="font-semibold mt-4">France (FR) — scenarios</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-3 font-semibold">Threshold</th>
                      <th className="py-2 pr-3 font-semibold text-right">% Qualifying</th>
                      <th className="py-2 pr-3 font-semibold text-right">Absorption</th>
                      <th className="py-2 pr-3 font-semibold text-right">Margin</th>
                      <th className="py-2 pr-3 font-semibold text-right">Uplift pool</th>
                      <th className="py-2 font-semibold text-center">Rating</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€175</td><td className="py-1.5 pr-3 text-right">52%</td><td className="py-1.5 pr-3 text-right">4.1%</td><td className="py-1.5 pr-3 text-right">22.4%</td><td className="py-1.5 pr-3 text-right">8.3%</td><td className="py-1.5 text-center text-red-500">Margin risk</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€200</td><td className="py-1.5 pr-3 text-right">46%</td><td className="py-1.5 pr-3 text-right">3.4%</td><td className="py-1.5 pr-3 text-right">23.1%</td><td className="py-1.5 pr-3 text-right">9.4%</td><td className="py-1.5 text-center text-yellow-500">Borderline</td></tr>
                    <tr className="border-b border-border/50 bg-primary/5"><td className="py-1.5 pr-3 font-semibold text-primary">€250 *</td><td className="py-1.5 pr-3 text-right font-semibold">39.1%</td><td className="py-1.5 pr-3 text-right font-semibold">2.7%</td><td className="py-1.5 pr-3 text-right font-semibold">24.1%</td><td className="py-1.5 pr-3 text-right font-semibold">10.1%</td><td className="py-1.5 text-center text-emerald-500 font-semibold">Recommended</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€275</td><td className="py-1.5 pr-3 text-right">36%</td><td className="py-1.5 pr-3 text-right">2.4%</td><td className="py-1.5 pr-3 text-right">24.5%</td><td className="py-1.5 pr-3 text-right">8.7%</td><td className="py-1.5 text-center text-muted-foreground">Also good (= B2B)</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€350</td><td className="py-1.5 pr-3 text-right">28%</td><td className="py-1.5 pr-3 text-right">1.9%</td><td className="py-1.5 pr-3 text-right">25.3%</td><td className="py-1.5 pr-3 text-right">6.2%</td><td className="py-1.5 text-center text-muted-foreground">Conservative</td></tr>
                  </tbody>
                </table>
              </div>

              {/* ── DE Simulation ── */}
              <h4 className="font-semibold mt-4">Germany (DE) — scenarios</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-3 font-semibold">Threshold</th>
                      <th className="py-2 pr-3 font-semibold text-right">% Qualifying</th>
                      <th className="py-2 pr-3 font-semibold text-right">Absorption</th>
                      <th className="py-2 pr-3 font-semibold text-right">Margin</th>
                      <th className="py-2 pr-3 font-semibold text-right">Uplift pool</th>
                      <th className="py-2 font-semibold text-center">Rating</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€195</td><td className="py-1.5 pr-3 text-right">49%</td><td className="py-1.5 pr-3 text-right">3.2%</td><td className="py-1.5 pr-3 text-right">23.3%</td><td className="py-1.5 pr-3 text-right">10.5%</td><td className="py-1.5 text-center text-yellow-500">= B2B, tight</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€225</td><td className="py-1.5 pr-3 text-right">45%</td><td className="py-1.5 pr-3 text-right">2.7%</td><td className="py-1.5 pr-3 text-right">23.8%</td><td className="py-1.5 pr-3 text-right">11.8%</td><td className="py-1.5 text-center text-muted-foreground">Decent</td></tr>
                    <tr className="border-b border-border/50 bg-primary/5"><td className="py-1.5 pr-3 font-semibold text-primary">€250 *</td><td className="py-1.5 pr-3 text-right font-semibold">42.1%</td><td className="py-1.5 pr-3 text-right font-semibold">2.3%</td><td className="py-1.5 pr-3 text-right font-semibold">24.1%</td><td className="py-1.5 pr-3 text-right font-semibold">12.9%</td><td className="py-1.5 text-center text-emerald-500 font-semibold">Recommended</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€300</td><td className="py-1.5 pr-3 text-right">35%</td><td className="py-1.5 pr-3 text-right">1.8%</td><td className="py-1.5 pr-3 text-right">25.0%</td><td className="py-1.5 pr-3 text-right">9.1%</td><td className="py-1.5 text-center text-muted-foreground">Higher margin, less reach</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€350</td><td className="py-1.5 pr-3 text-right">28%</td><td className="py-1.5 pr-3 text-right">1.5%</td><td className="py-1.5 pr-3 text-right">25.8%</td><td className="py-1.5 pr-3 text-right">7.3%</td><td className="py-1.5 text-center text-muted-foreground">Too conservative</td></tr>
                  </tbody>
                </table>
              </div>

              {/* ── GB Simulation ── */}
              <h4 className="font-semibold mt-4">United Kingdom (GB) — scenarios</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-3 font-semibold">Threshold</th>
                      <th className="py-2 pr-3 font-semibold text-right">% Qualifying</th>
                      <th className="py-2 pr-3 font-semibold text-right">Absorption</th>
                      <th className="py-2 pr-3 font-semibold text-right">Margin</th>
                      <th className="py-2 pr-3 font-semibold text-right">Uplift pool</th>
                      <th className="py-2 font-semibold text-center">Rating</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€300</td><td className="py-1.5 pr-3 text-right">34%</td><td className="py-1.5 pr-3 text-right">5.2%</td><td className="py-1.5 pr-3 text-right">21.8%</td><td className="py-1.5 pr-3 text-right">13.4%</td><td className="py-1.5 text-center text-red-500">Margin danger</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€350</td><td className="py-1.5 pr-3 text-right">28%</td><td className="py-1.5 pr-3 text-right">4.7%</td><td className="py-1.5 pr-3 text-right">23.6%</td><td className="py-1.5 pr-3 text-right">12.1%</td><td className="py-1.5 text-center text-yellow-500">Borderline</td></tr>
                    <tr className="border-b border-border/50 bg-primary/5"><td className="py-1.5 pr-3 font-semibold text-primary">€400 *</td><td className="py-1.5 pr-3 text-right font-semibold">22.9%</td><td className="py-1.5 pr-3 text-right font-semibold">4.3%</td><td className="py-1.5 pr-3 text-right font-semibold">26.0%</td><td className="py-1.5 pr-3 text-right font-semibold">11.1%</td><td className="py-1.5 text-center text-emerald-500 font-semibold">Recommended</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€500</td><td className="py-1.5 pr-3 text-right">17%</td><td className="py-1.5 pr-3 text-right">3.5%</td><td className="py-1.5 pr-3 text-right">27.2%</td><td className="py-1.5 pr-3 text-right">8.8%</td><td className="py-1.5 text-center text-muted-foreground">Low reach</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">€720</td><td className="py-1.5 pr-3 text-right">8%</td><td className="py-1.5 pr-3 text-right">2.1%</td><td className="py-1.5 pr-3 text-right">28.4%</td><td className="py-1.5 pr-3 text-right">4.2%</td><td className="py-1.5 text-center text-muted-foreground">= B2B, too high</td></tr>
                  </tbody>
                </table>
              </div>

              <InfoBox variant="formula">
                <p className="font-semibold">How to read these simulations</p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-muted-foreground">
                  <li><strong>% Qualifying:</strong> share of orders at or above the threshold &rarr; target 20-50%</li>
                  <li><strong>Absorption:</strong> shipping cost absorbed as % of qualifying revenue &rarr; keep under 5%</li>
                  <li><strong>Margin:</strong> contribution margin after absorbing shipping &rarr; keep above 23%</li>
                  <li><strong>Uplift pool:</strong> orders within 20% below threshold &rarr; higher = more AOV growth potential</li>
                  <li><strong>* = recommended</strong> — best balance of all four metrics</li>
                </ul>
              </InfoBox>

              <h4 className="font-semibold mt-4">Revenue impact model</h4>
              <InfoBox variant="formula">
                <p className="font-mono text-xs space-y-1">
                  <span className="block"><strong>Annual shipping absorbed</strong> = qualifying_orders &times; avg_shipping_cost &times; 52 weeks</span>
                  <span className="block"><strong>Annual uplift revenue</strong> = uplift_orders &times; avg_gap &times; conversion_rate &times; 52</span>
                  <span className="block"><strong>Net impact</strong> = uplift_revenue &times; gross_margin% &minus; shipping_absorbed</span>
                </p>
                <div className="mt-3 text-muted-foreground">
                  <p>Conservative estimate using 30% uplift conversion (industry average is 58%):</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li><strong>NL:</strong> ~€8K absorbed vs ~€12K uplift revenue = <span className="text-emerald-600 dark:text-emerald-400">+€4K net/year</span></li>
                    <li><strong>FR:</strong> ~€11K absorbed vs ~€18K uplift revenue = <span className="text-emerald-600 dark:text-emerald-400">+€7K net/year</span></li>
                    <li><strong>DE:</strong> ~€4K absorbed vs ~€8K uplift revenue = <span className="text-emerald-600 dark:text-emerald-400">+€4K net/year</span></li>
                    <li><strong>GB:</strong> ~€9K absorbed vs ~€14K uplift revenue = <span className="text-emerald-600 dark:text-emerald-400">+€5K net/year</span></li>
                  </ul>
                  <p className="mt-2 font-medium text-foreground">Total estimated net gain: ~€20K/year (conservative)</p>
                </div>
              </InfoBox>
            </div>
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════
            7. B2B vs B2C
           ════════════════════════════════════════════════════════════ */}
        {isVisible('b2b-vs-b2c') && (
          <Section
            id="b2b-vs-b2c"
            icon={Users}
            title="B2B vs B2C Considerations"
            description="How thresholds differ between business and consumer channels"
            highlight={isHighlighted('b2b-vs-b2c')}
          >
            <div className="space-y-5 text-sm text-foreground leading-relaxed">

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-3 font-semibold">Dimension</th>
                      <th className="py-2 pr-3 font-semibold">B2C (consumer)</th>
                      <th className="py-2 font-semibold">B2B (wholesale)</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-border/50">
                      <td className="py-2 pr-3 font-medium text-foreground">Primary goal</td>
                      <td className="py-2 pr-3">Increase AOV via psychological nudge</td>
                      <td className="py-2">Ensure minimum order covers shipping + handling</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-2 pr-3 font-medium text-foreground">Typical AOV</td>
                      <td className="py-2 pr-3">€150-350</td>
                      <td className="py-2">€500-2,000+</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-2 pr-3 font-medium text-foreground">Shipping method</td>
                      <td className="py-2 pr-3">Parcel (€8-30)</td>
                      <td className="py-2">Parcel or pallet (€10-200+)</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-2 pr-3 font-medium text-foreground">Margin</td>
                      <td className="py-2 pr-3">~30% gross</td>
                      <td className="py-2">~15-20% gross (narrower)</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-2 pr-3 font-medium text-foreground">Threshold approach</td>
                      <td className="py-2 pr-3">Fixed amount, clearly visible</td>
                      <td className="py-2">Often MOQ-based or negotiated per-account</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-2 pr-3 font-medium text-foreground">Customer behavior</td>
                      <td className="py-2 pr-3">Emotional, impulse-driven, adds small items</td>
                      <td className="py-2">Rational, planned, price-sensitive</td>
                    </tr>
                    <tr className="border-b border-border/50">
                      <td className="py-2 pr-3 font-medium text-foreground">Repeat rate</td>
                      <td className="py-2 pr-3">Variable, loyalty-dependent</td>
                      <td className="py-2">High, recurring orders</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <InfoBox variant="note">
                <p className="font-semibold">Current state in Shippy</p>
                <p className="mt-1 text-muted-foreground">
                  Today, B2B francos are set per-country in the <code className="text-xs bg-muted px-1 py-0.5 rounded">ShippingCountry.franco_threshold</code> field.
                  B2C francos use the separate <code className="text-xs bg-muted px-1 py-0.5 rounded">franco_b2c</code> field — currently only populated for Belgium.
                  The B2C recommendations in this analysis are independent of B2B thresholds and optimized for consumer behavior patterns.
                </p>
              </InfoBox>

              <h4 className="font-semibold mt-2">Why B2C thresholds should be lower than B2B</h4>
              <p className="text-muted-foreground">
                It may seem counterintuitive, but B2C thresholds are typically <strong>lower</strong> than B2B despite B2C having higher margins. The reason:
                B2C relies on the <strong>psychological uplift effect</strong> (adding items to cart), which works best when the threshold is close to median order value.
                B2B thresholds serve a <strong>cost-coverage function</strong> — they need to be high enough that every qualifying order's margin absorbs the shipping cost.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-3 font-semibold">Country</th>
                      <th className="py-2 pr-3 font-semibold text-right">B2B Franco</th>
                      <th className="py-2 pr-3 font-semibold text-right">B2C Recommended</th>
                      <th className="py-2 font-semibold text-right">Difference</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">NL</td><td className="py-1.5 pr-3 text-right">€215</td><td className="py-1.5 pr-3 text-right">€200</td><td className="py-1.5 text-right">-7%</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">BE</td><td className="py-1.5 pr-3 text-right">—</td><td className="py-1.5 pr-3 text-right">€230</td><td className="py-1.5 text-right">—</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">FR</td><td className="py-1.5 pr-3 text-right">€275</td><td className="py-1.5 pr-3 text-right">€250</td><td className="py-1.5 text-right">-9%</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">DE</td><td className="py-1.5 pr-3 text-right">€195</td><td className="py-1.5 pr-3 text-right">€250</td><td className="py-1.5 text-right text-yellow-500">+28%</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">GB</td><td className="py-1.5 pr-3 text-right">€720</td><td className="py-1.5 pr-3 text-right">€400</td><td className="py-1.5 text-right">-44%</td></tr>
                  </tbody>
                </table>
              </div>

              <InfoBox variant="important">
                <p className="font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Germany anomaly</p>
                <p className="mt-1 text-muted-foreground">
                  Germany is the only country where the B2C recommendation (€250) is <strong>higher</strong> than the B2B franco (€195). This is because the B2B threshold appears to be set too low — at €195, the margin after absorption is only 23.3%. Consider raising the B2B franco for DE as well.
                </p>
              </InfoBox>
            </div>
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════
            8. CROSS-BORDER
           ════════════════════════════════════════════════════════════ */}
        {isVisible('cross-border') && (
          <Section
            id="cross-border"
            icon={Truck}
            title="Cross-Border & EU Considerations"
            description="Regulatory landscape and international shipping factors"
            highlight={isHighlighted('cross-border')}
          >
            <div className="space-y-5 text-sm text-foreground leading-relaxed">

              <h4 className="font-semibold">Why one threshold doesn't fit all</h4>
              <p className="text-muted-foreground">
                Shipping costs vary dramatically by destination. Our data confirms this — UK shipping (€29 avg) is <strong>3x more expensive</strong> than NL/BE (€9-10 avg). Industry best practice is to set region-specific thresholds roughly <strong>20-30% above each region's AOV</strong> rather than a universal number [12].
              </p>

              <h4 className="font-semibold mt-4">Shipping cost comparison</h4>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {COUNTRIES.map((c) => (
                  <MetricCard key={c.iso} label={`${c.flag} ${c.iso} avg shipping`} value={`€${c.avgShip.toFixed(0)}`} sub={`median €${c.medianShip.toFixed(0)}`} />
                ))}
              </div>

              <h4 className="font-semibold mt-4">EU regulatory changes (July 2026)</h4>
              <InfoBox variant="important">
                <p className="font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Upcoming: EUR 150 duty exemption removal</p>
                <p className="mt-2 text-muted-foreground">
                  The EU is eliminating the EUR 150 customs duty exemption for non-EU goods and adding a <strong>€3 handling fee per parcel</strong>. While this primarily affects goods imported from outside the EU, it increases overall shipping cost benchmarks and consumer sensitivity to shipping charges. Our recommendations already account for this trend.
                </p>
              </InfoBox>

              <h4 className="font-semibold mt-4">UK-specific factors</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Post-Brexit VAT: collected at checkout for orders under GBP 135</li>
                <li>Customs declarations add ~€2-5 per shipment in processing costs</li>
                <li>Carrier fuel surcharges are typically higher for cross-channel routes</li>
                <li>GBP/EUR exchange rate fluctuations add ~2-5% cost variability</li>
                <li>The €400 threshold accounts for all of these by building in extra margin buffer (26% vs 24% for EU)</li>
              </ul>

              <h4 className="font-semibold mt-4">Carrier cost trends (2024-2026)</h4>
              <p className="text-muted-foreground">
                Major carriers (UPS, FedEx, DHL) have implemented <strong>5.9% annual rate increases</strong> for three consecutive years [10]. Per-package ground delivery rates are now <strong>31% above 2022 baseline</strong>. This means our thresholds should be reviewed at least annually and adjusted upward if shipping costs continue rising.
              </p>

              <InfoBox variant="note">
                <p className="font-semibold">Currency display</p>
                <p className="mt-1 text-muted-foreground">
                  All thresholds in this analysis are in EUR. For UK customers, the franco should be displayed in GBP at the prevailing exchange rate. Shippy already handles this conversion in the B2C checkout flow via the Shopify carrier service endpoint.
                </p>
              </InfoBox>
            </div>
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════
            9. IMPLEMENTATION PLAYBOOK
           ════════════════════════════════════════════════════════════ */}
        {isVisible('implementation') && (
          <Section
            id="implementation"
            icon={Layers}
            title="Implementation Playbook"
            description="Step-by-step rollout plan and testing strategy"
            highlight={isHighlighted('implementation')}
          >
            <div className="space-y-5 text-sm text-foreground leading-relaxed">

              <h4 className="font-semibold">Phase 1: Set thresholds in Shippy (Day 1)</h4>
              <p className="text-muted-foreground">
                Update the <code className="text-xs bg-muted px-1 py-0.5 rounded">franco_b2c</code> field in Shipping Config for each country:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="py-2 pr-3 font-semibold">Country</th>
                      <th className="py-2 pr-3 font-semibold">Action</th>
                      <th className="py-2 font-semibold text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">NL</td><td className="py-1.5 pr-3">Set <code className="text-xs bg-muted px-1 py-0.5 rounded">franco_b2c</code></td><td className="py-1.5 text-right font-medium">€200</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">BE</td><td className="py-1.5 pr-3">No change</td><td className="py-1.5 text-right font-medium">€230 (already set)</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">FR</td><td className="py-1.5 pr-3">Set <code className="text-xs bg-muted px-1 py-0.5 rounded">franco_b2c</code></td><td className="py-1.5 text-right font-medium">€250</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">DE</td><td className="py-1.5 pr-3">Set <code className="text-xs bg-muted px-1 py-0.5 rounded">franco_b2c</code></td><td className="py-1.5 text-right font-medium">€250</td></tr>
                    <tr className="border-b border-border/50"><td className="py-1.5 pr-3">GB</td><td className="py-1.5 pr-3">Set <code className="text-xs bg-muted px-1 py-0.5 rounded">franco_b2c</code></td><td className="py-1.5 text-right font-medium">€400</td></tr>
                  </tbody>
                </table>
              </div>

              <h4 className="font-semibold mt-4">Phase 2: Shopify storefront (Day 1-3)</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Add free-shipping progress bar to cart page ("Add €X more for free shipping!")</li>
                <li>Display free-shipping badge on qualifying orders at checkout</li>
                <li>Update shipping rate names (e.g., "Free Standard Shipping" vs "Standard Shipping €X.XX")</li>
              </ul>

              <InfoBox variant="note">
                <p className="font-semibold flex items-center gap-2"><Lightbulb className="h-4 w-4" /> Pro tip: progress bars</p>
                <p className="mt-1 text-muted-foreground">
                  Industry data shows that presenting a progress indicator toward the free shipping threshold converts <strong>48% of shoppers who initially planned to reconsider</strong> [3]. This is the single highest-ROI UX change you can make.
                </p>
              </InfoBox>

              <h4 className="font-semibold mt-4">Phase 3: Monitor (Week 1-4)</h4>
              <p className="text-muted-foreground">Track these KPIs weekly against the baseline period:</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <MetricCard label="AOV by country" value="Track" sub="target +10-15%" />
                <MetricCard label="Conversion rate" value="Track" sub="expect -2 to +5%" />
                <MetricCard label="Shipping cost absorption" value="Track" sub="keep < 5% of revenue" />
                <MetricCard label="Free shipping uptake" value="Track" sub="target 35-50%" />
                <MetricCard label="Cart size (items)" value="Track" sub="expect +0.3-0.5 items" />
                <MetricCard label="Revenue per session" value="Track" sub="net impact metric" />
              </div>

              <h4 className="font-semibold mt-4">Phase 4: Optimize (Month 2+)</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>A/B test threshold variations (e.g., €200 vs €225 for NL) if volume supports it</li>
                <li>Consider seasonal adjustments (lower thresholds during sales events)</li>
                <li>Review quarterly against updated shipping costs — carrier rates are rising ~6%/year</li>
                <li>Evaluate adding a "nearly there" email trigger for abandoned carts close to threshold</li>
              </ul>

              <InfoBox variant="success">
                <p className="font-semibold flex items-center gap-2"><CheckCircle className="h-4 w-4" /> Success criteria</p>
                <ul className="list-disc list-inside mt-2 space-y-1 text-muted-foreground">
                  <li>AOV increases by at least 5% in target countries within 30 days</li>
                  <li>Conversion rate does not drop more than 3%</li>
                  <li>Net revenue per session is positive (AOV lift &gt; conversion drop)</li>
                  <li>Shipping absorption stays below 5% of qualifying revenue</li>
                </ul>
              </InfoBox>
            </div>
          </Section>
        )}

        {/* ════════════════════════════════════════════════════════════
            10. SOURCES
           ════════════════════════════════════════════════════════════ */}
        {isVisible('sources') && (
          <Section
            id="sources"
            icon={BookOpen}
            title="Sources & References"
            description="All data cited in this analysis"
            highlight={isHighlighted('sources')}
          >
            <div className="space-y-3 text-sm text-foreground leading-relaxed">
              <p className="text-muted-foreground mb-4">
                References combine our internal Shippy data ({TOTAL_ORDERS} orders) with external industry research. Numbers in brackets [N] correspond to citations throughout the document.
              </p>

              <div className="space-y-2">
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[1]</span><SourceLink num={1} url="https://capitaloneshopping.com/research/free-shipping-statistics/" title="Capital One Shopping — Free Shipping Statistics (2025)" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[2]</span><SourceLink num={2} url="https://www.convertcart.com/blog/free-shipping-for-conversion-rates" title="ConvertCart — Free Shipping for Conversion Rates (UPS/ComScore data)" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[3]</span><SourceLink num={3} url="https://resources.rework.com/libraries/ecommerce-growth/free-shipping-thresholds" title="Rework — Free Shipping Thresholds Best Practices (2025)" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[4]</span><SourceLink num={4} url="https://redstagfulfillment.com/what-percentage-of-ecommerce-orders-offer-free-shipping/" title="Red Stag Fulfillment — E-commerce Free Shipping Statistics" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[5]</span><SourceLink num={5} url="https://baymard.com/lists/cart-abandonment-rate" title="Baymard Institute — Cart Abandonment Rate Statistics" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[6]</span><SourceLink num={6} url="https://www.portless.com/blogs/free-shipping-strategy-ecommerce-2026" title="Portless — Free Shipping Strategy for E-commerce 2026" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[7]</span><SourceLink num={7} url="https://www.sellerscommerce.com/blog/free-shipping-statistics/" title="SellersCommerce — 15 Important Free Shipping Statistics (2025)" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[8]</span><SourceLink num={8} url="https://www.goinflow.com/blog/ecommerce-free-shipping/" title="GoInflow — Free Shipping & Conversion Rate Impact" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[9]</span><SourceLink num={9} url="https://www.swell.is/content/global-shipping-ecommerce-statistics" title="Swell — Global Shipping E-commerce Statistics" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[10]</span><SourceLink num={10} url="https://www.partnership.com/blog/post/fedex-ups-general-rate-increase" title="PartnerShip — FedEx/UPS General Rate Increase (2024-2026)" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[11]</span><SourceLink num={11} url="https://www.cmswire.com/customer-experience/what-prime-day-2025-reveals-about-shipping-returns-and-customer-expectations/" title="CMSWire — What Prime Day 2025 Reveals About Shipping Expectations" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[12]</span><SourceLink num={12} url="https://warpdriven.ai/en/blog/industry-1/free-shipping-thresholds-best-practices-region-2025-99" title="WarpDriven — Free Shipping Thresholds Best Practices by Region (2025)" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[13]</span><SourceLink num={13} url="https://www.shopify.com/blog/free-shipping-and-conversion" title="Shopify — Free Shipping Guide: Strategies to Boost Sales (2026)" /></div>
                <div className="flex gap-2"><span className="text-primary font-mono text-xs w-6 shrink-0">[14]</span><SourceLink num={14} url="https://www.fermatcommerce.com/post/free-shipping-threshold" title="Fermat Commerce — Free Shipping Threshold Strategy" /></div>
              </div>

              <InfoBox variant="default">
                <p className="text-muted-foreground">
                  <strong>Internal data:</strong> All country-specific metrics (AOV, shipping costs, qualification rates, margins, uplift pools) are computed from Shippy's <code className="text-xs bg-muted px-1 py-0.5 rounded">PackingAnalysisRecord</code> table using the <code className="text-xs bg-muted px-1 py-0.5 rounded">franco_analysis.py</code> script. Analysis date: {ANALYSIS_DATE}.
                </p>
              </InfoBox>
            </div>
          </Section>
        )}

      </div>
    </div>
  );
};

export default FrancoAnalysis;
